import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  useMediaQuery
} from '@mui/material';
import {
  Login as LoginIcon,
  Menu as MenuIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import SettingsIcon from '@mui/icons-material/Settings';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import InventoryIcon from '@mui/icons-material/Inventory';
import FactoryIcon from '@mui/icons-material/Factory';
import PeopleIcon from '@mui/icons-material/People';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';

const Navigation = ({ onNavigateToAuth }) => {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));

  const handleItemClick = (item) => {
    setIsOpen(false);

    if (item === 'Tasks') {
      navigate('/task-manager');
    } else if (item === 'Calendar') {
      navigate('/company-calendar');
    } else if (item === 'Schedule') {
      navigate('/schedule-management');
    } else if (item === 'CRM') {
      navigate('/crm');
    } else if (item === 'Price Lists') {
      navigate('/price-lists');
    } else if (item === 'Brand Management') {
      navigate('/brand-management');
    } else if (item === 'Product Catalog') {
      navigate('/product-catalog');
    } else if (item === 'Formulations/R&D') {
      navigate('/formulations');
    } else if (item === 'Quality Control') {
      navigate('/quality-control');
    } else if (item === 'Vendor Management') {
      navigate('/vendor-management');
    } else if (item === 'Inventory') {
      navigate('/inventory');
    } else if (item === 'Batch Production') {
      navigate('/batch-production');
    } else if (item === 'Line Production') {
      navigate('/line-production');
    } else if (item === 'Employees') {
      navigate('/employees');
    } else if (item === 'User Management') {
      navigate('/user-management');
    }

    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'instant' });
    }, 100);
  };

  const categories = [
    {
      title: 'Operations',
      color: '#2563eb',
      icon: SettingsIcon,
      items: ['Tasks', 'Calendar', 'Schedule']
    },
    {
      title: 'Sales',
      color: '#059669',
      icon: MonetizationOnIcon,
      items: ['CRM', 'Price Lists']
    },
    {
      title: 'Products',
      color: '#dc2626',
      icon: InventoryIcon,
      items: ['Brand Management', 'Product Catalog', 'Formulations/R&D']
    },
    {
      title: 'Inventory',
      color: '#f59e0b',
      icon: InventoryIcon,
      items: ['Inventory', 'Quality Control', 'Vendor Management']
    },
    {
      title: 'Manufacturing',
      color: '#7c3aed',
      icon: FactoryIcon,
      items: ['Batch Production', 'Line Production']
    },
    {
      title: 'Administration',
      color: '#ea580c',
      icon: PeopleIcon,
      items: ['Employees', 'User Management']
    }
  ];

  return (
    <>
    <AppBar 
      position="fixed" 
      elevation={0}
      sx={{ 
          background: 'rgba(255, 255, 255, 0.98)',
        backdropFilter: 'blur(10px)',
          borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
          zIndex: 1100
      }}
    >
        <Toolbar
          sx={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            py: { xs: 1, md: 1.5 },
            px: { xs: 2, md: 4 },
            minHeight: { xs: 56, md: 64 }
          }}
        >
          <Box sx={{ flex: { xs: 0, md: 1 } }}>
            <Typography
              variant="h6"
              onClick={() => window.location.href = '/'}
              sx={{
                fontWeight: 500,
                color: '#2d3748',
                fontSize: { xs: '1.25rem', md: '1.5rem' },
                letterSpacing: '0.5px',
                cursor: 'pointer',
                '&:hover': {
                  color: '#1e3a8a'
                }
              }}
            >
              GlowWorks
            </Typography>
          </Box>

          {!isMobile && (
            <Box sx={{ 
              position: 'absolute', 
              left: '50%', 
              transform: 'translateX(-50%)',
              display: 'flex',
              justifyContent: 'center'
            }}>
              <Button
                onClick={() => setIsOpen(!isOpen)}
                endIcon={isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                sx={{
                  color: '#2d3748',
                  fontWeight: 500,
                  fontSize: '1rem',
                  textTransform: 'none',
                  px: 2.5,
                  py: 1,
                  border: '1px solid rgba(0, 0, 0, 0.1)',
                  borderRadius: 1,
                  backgroundColor: isOpen ? 'rgba(37, 99, 235, 0.05)' : 'white',
                  '&:hover': {
                    backgroundColor: 'rgba(37, 99, 235, 0.08)',
                    borderColor: 'rgba(37, 99, 235, 0.3)'
                  }
                }}
              >
                Features
              </Button>
            </Box>
          )}

          <Box sx={{ 
            flex: { xs: 0, md: 1 },
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: { xs: 'flex-end', md: 'flex-end' },
            gap: { xs: 1, md: 2 } 
          }}>
            {isMobile && (
              <IconButton
                onClick={() => setIsOpen(!isOpen)}
                sx={{
                  color: '#2d3748',
                  border: '1px solid rgba(0, 0, 0, 0.1)',
                  borderRadius: 1,
                  backgroundColor: isOpen ? 'rgba(37, 99, 235, 0.05)' : 'white',
                  '&:hover': {
                    backgroundColor: 'rgba(37, 99, 235, 0.08)'
                  }
                }}
              >
                {isOpen ? <CloseIcon /> : <MenuIcon />}
              </IconButton>
            )}

            <Button
              variant="contained"
              startIcon={!isMobile && <LoginIcon />}
              onClick={onNavigateToAuth}
              sx={{
                backgroundColor: '#2d3748',
                color: '#ffffff',
                fontWeight: 500,
                px: { xs: 2, md: 3 },
                py: { xs: 0.75, md: 1 },
                textTransform: 'none',
                borderRadius: 1.5,
                fontSize: { xs: '0.875rem', md: '1rem' },
                minWidth: { xs: 'auto', md: 'auto' },
                '&:hover': {
                  backgroundColor: '#1a202c'
                }
              }}
            >
              {isMobile ? <LoginIcon /> : 'Sign in'}
            </Button>
          </Box>
        </Toolbar>
      </AppBar>

                {isOpen && (
        <Box
                    onClick={() => setIsOpen(false)}
          sx={{
                      position: 'fixed',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
            zIndex: 1050,
            backgroundColor: { xs: 'rgba(0, 0, 0, 0.5)', md: 'rgba(0, 0, 0, 0.2)' }
                    }}
                  />
                )}

      <Box
        sx={{
          position: 'fixed',
          top: { xs: 56, md: 64 },
          left: 0,
          right: 0,
          bottom: { xs: 0, md: 'auto' },
          backgroundColor: 'white',
          borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
          transform: isOpen ? 'translateY(0)' : { xs: 'translateX(100%)', md: 'translateY(-100%)' },
          opacity: isOpen ? 1 : 0,
          visibility: isOpen ? 'visible' : 'hidden',
          transition: 'all 0.3s ease',
          zIndex: 1060,
          py: { xs: 3, md: 6 },
          px: { xs: 2, md: 4 },
          overflowY: 'auto',
          maxHeight: { xs: 'calc(100vh - 56px)', md: '70vh' }
        }}
      >
        <Box
          sx={{
            maxWidth: 1400,
            margin: '0 auto',
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(3, 1fr)',
              lg: 'repeat(4, 1fr)'
            },
            gap: { xs: 3, sm: 4, md: 6, lg: 8 }
          }}
        >
          {categories.map((category, index) => (
            <Box key={index}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
                  gap: 1,
                  mb: { xs: 2, md: 2.5 },
                  pb: 1,
                  borderBottom: `2px solid ${category.color}`
                }}
              >
                <category.icon sx={{ fontSize: { xs: 22, md: 20 }, color: category.color }} />
                <Typography
                  sx={{
                    fontWeight: 500,
                    fontSize: { xs: '1rem', md: '0.95rem' },
                    color: category.color,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}
                >
                  {category.title}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 0, md: 0.5 } }}>
                {category.items.map((item, itemIndex) => (
                  <Typography
                    key={itemIndex}
                    onClick={() => handleItemClick(item)}
              sx={{
                      fontSize: { xs: '0.95rem', md: '0.9rem' },
                      color: '#4b5563',
                      cursor: 'pointer',
                      py: { xs: 1.25, md: 0.75 },
                      px: { xs: 2, md: 1.5 },
                      borderRadius: 0.5,
                      transition: 'all 0.2s',
                '&:hover': {
                        backgroundColor: `${category.color}10`,
                        color: '#1f2937',
                        transform: 'translateX(4px)'
                      },
                      '&:active': {
                        backgroundColor: `${category.color}20`
                      }
                    }}
                  >
                    {item}
                  </Typography>
                ))}
              </Box>
            </Box>
          ))}
        </Box>
          </Box>
    </>
  );
};

export default Navigation;